import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import ScreenTransition from './ScreenTransition';

function About() {
  const navigate = useNavigate();

  return (
    <ScreenTransition direction="right">
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-zinc-900/60 px-4 py-6 sm:p-8 rounded-2xl max-w-4xl w-full overflow-y-auto max-h-[90vh]">
          <div className="mb-6 flex flex-col items-center">
            <Button onClick={() => navigate('/')} className="w-32 mb-6">
              Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-yellow-300">
              About Wall Go
            </h1>
          </div>

          <div className="space-y-6 text-gray-200 text-sm sm:text-base leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">What is Wall Go?</h2>
              <p className="mb-3">
                Wall Go is a strategic board game inspired by the popular Korean reality show "The Devil's Plan" (데블스 플랜). While the game draws aesthetic inspiration from Baduk (Go/Weiqi) with its black and white stones on a grid board, the core mechanics are more similar to Quoridor—a modern abstract strategy game that emphasizes movement and tactical wall placement.
              </p>
              <p>
                The game challenges players to think strategically about movement, spatial relationships, and the tactical placement of walls to create barriers and control territory. Players must balance offensive advancement with defensive wall placement to outmaneuver their opponent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Similarity to Quoridor</h2>
              <p className="mb-3">
                Wall Go shares its core mechanics with <a href="https://en.wikipedia.org/wiki/Quoridor" target="_blank" rel="noopener noreferrer" className="text-yellow-300 hover:text-yellow-400 underline">Quoridor</a>, a two-player abstract strategy game designed by Mirko Marchesi and published by Gigamic Games in 1997. Quoridor won the Mensa Mind Game award in 1997 and has been recognized as Game of the Year in multiple countries.
              </p>
              <p className="mb-3">
                Like Quoridor, Wall Go features a grid-based board where players control pieces (stones in Wall Go, pawns in Quoridor) and can either move their pieces or place walls on their turn. The strategic depth comes from deciding when to advance and when to block your opponent's path. Walls, once placed, cannot be moved and affect all players' movement.
              </p>
              <p>
                While Quoridor focuses on reaching the opposite side of the board, Wall Go adds a territory control element, where players score points based on the areas they control, separated by walls. This creates a unique strategic experience that combines pathfinding with territorial control.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Aesthetic Inspiration from Baduk</h2>
              <p className="mb-3">
                While Wall Go's mechanics are based on Quoridor, the game's visual design draws inspiration from Baduk (known as Go in Japan and Weiqi in China)—one of the world's oldest and most revered strategy games, originating in China over 2,500 - 4,000 years ago.
              </p>
              <p>
                The use of black and white stones on a grid board creates a familiar aesthetic for players who know Baduk, even though the rules and objectives are quite different. This visual connection to the ancient game adds an elegant, timeless quality to Wall Go's presentation.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Inspiration from The Devil's Plan</h2>
              <p className="mb-3">
                "The Devil's Plan" (데블스 플랜) is a South Korean reality competition show that premiered in 2023 on Netflix. Created by producer Jeong Jong-yeon, the show features 12 contestants competing in a series of intellectual games, puzzles, and strategic challenges over the course of a week, all competing for a grand prize.
              </p>
              <p className="mb-3">
                The show is known for its intense strategic gameplay, psychological elements, and complex social dynamics as contestants form alliances and betrayals. One of the standout games featured in the series was a strategic board game that combined elements of traditional board games with wall-building mechanics, creating dramatic and suspenseful moments.
              </p>
              <p>
                Wall Go was created to bring this exciting game variant to a wider audience, allowing players worldwide to experience the strategic depth and excitement that made the game so compelling on the show. The combination of movement, wall placement, and territory control creates a unique strategic experience that captures the intellectual challenge and psychological tension of "The Devil's Plan."
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">How to Play</h2>
              <p className="mb-3">
                Wall Go is played on a grid board by two players, each controlling stones of one color (black or white). The game consists of several phases:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li><strong>Stone Placement Phase:</strong> Players take turns placing their stones on empty intersections of the board. Black moves first.</li>
                <li><strong>Movement Phase:</strong> After all stones are placed, players take turns moving their stones. Each stone can move one or two steps horizontally or vertically, as long as the path is clear.</li>
                <li><strong>Wall Placement:</strong> After moving a stone, the player must place a wall around it. Walls can be placed horizontally or vertically and cannot overlap with existing walls.</li>
                <li><strong>Victory Condition:</strong> The game ends when all stones of opposite colors are separated by walls, or when stones cannot move. Players score points based on the territory they control.</li>
              </ul>
              <p>
                Each player has a time limit to make their moves, adding an element of time pressure to the strategic decision-making process.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Strategic Tips and Gameplay</h2>
              <p className="mb-3">
                Success in Wall Go requires a combination of strategic thinking, spatial awareness, and tactical planning. Here are some key strategies to consider:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li><strong>Territory Control:</strong> Focus on controlling key areas of the board early. Think about which regions will be most valuable for scoring points.</li>
                <li><strong>Wall Placement:</strong> Use walls strategically to both protect your own territory and limit your opponent's movement options. A well-placed wall can cut off enemy stones from valuable areas.</li>
                <li><strong>Stone Mobility:</strong> Keep your stones mobile and avoid getting trapped. Stones that can move freely are more valuable than isolated pieces.</li>
                <li><strong>Time Management:</strong> With the time limit in place, practice making quick but thoughtful decisions. Don't spend too much time on early moves, but be careful with critical decisions.</li>
                <li><strong>Reading Ahead:</strong> Try to anticipate your opponent's moves and plan several turns in advance. This is especially important in the movement and wall-placement phases.</li>
                <li><strong>Balance:</strong> Balance between aggressive expansion and defensive consolidation. Sometimes it's better to secure a smaller territory than to overextend.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Game Features</h2>
              <p className="mb-3">
                Wall Go offers several features to enhance your gaming experience:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li><strong>Two-Player Mode:</strong> Play against a friend locally on the same device, perfect for face-to-face strategic battles.</li>
                <li><strong>AI Opponent:</strong> Practice against computer opponents with different difficulty levels. Challenge yourself against increasingly skilled AI players.</li>
                <li><strong>Clear Visual Design:</strong> The game features an intuitive interface that makes it easy to understand the board state and plan your moves.</li>
                <li><strong>Time Management:</strong> Built-in timers help you practice managing your time effectively, an important skill in competitive play.</li>
                <li><strong>No Registration Required:</strong> Jump right into the game without creating an account or providing personal information.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Technical Information</h2>
              <p className="mb-3">
                Wall Go is built as a modern web application using React, ensuring compatibility across different devices and browsers. The game runs entirely in your browser, requiring no downloads or installations. All game data is processed locally, ensuring fast performance and privacy.
              </p>
              <p>
                The application is designed to be responsive and work well on both desktop and mobile devices, allowing you to enjoy Wall Go wherever you are.
              </p>
            </section>
          </div>
        </div>
      </div>
    </ScreenTransition>
  );
}

export default About;
